import 'whatwg-fetch';
import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import store from 'store';
import US_STATES from '../../constants/UsStates';
import * as Onboarding from '../../analytics/Onboarding';
import FormikErrorTracker from '../../analytics/FormikErrorTracker';
import AddressAutocomplete from './AddressAutocomplete';
import '../signup/Signup.scss';

class StepOneForm extends React.Component {
  state = {
    fullName: '',
    street1: '',
    street2: '',
    city: '',
    state: '',
    zipCode: '',
    showStreet2: !!(store.get('userInfo') && store.get('userInfo').street2),
    showCityState: !!(store.get('userInfo') && store.get('userInfo').city)
  };

  async verifyAddress(inputData) {
    const data = JSON.stringify({ address: inputData });
    const response = await fetch(`/api/address/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: data
    });

    const responseData = await response.json();
    if (responseData.status === 200) {
      const { result } = responseData;
      const { components, deliverability } = result;
      return {
        deliverability,
        street1: result.primary_line,
        street2: result.secondary_line,
        city: components.city,
        state: components.state,
        zipCode: components.zip_code
      };
    } else {
      return null;
    }
  }

  async lookupZip(zip, setFieldValue) {
    if (!/^\d{5}$/.test(zip)) return;
    try {
      const response = await fetch(`/api/address/zip-lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ zip })
      });
      const data = await response.json();
      if (data.status === 200) {
        setFieldValue('city', data.city, false);
        setFieldValue('state', data.state, false);
      }
    } catch (e) {
      // silently fail — user can fill in manually
    }
  }

  render() {
    let initialValues = {
      fullName: this.state.fullName,
      street1: this.state.street1,
      street2: this.state.street2,
      city: this.state.city,
      state: this.state.state,
      zipCode: this.state.zipCode
    };
    const userInfo = store.get('userInfo');
    if (userInfo) {
      initialValues = { ...initialValues, ...userInfo };
    }

    return (
      <div>
        <Formik
          validateOnChange={false}
          validateOnBlur={true}
          initialValues={initialValues}
          validate={values => {
            let errors = {};
            if (!values.fullName || !values.fullName.trim()) {
              errors.fullName = 'Please enter your full name.';
            }
            if (!values.street1) {
              errors.street1 = 'Please enter the street address.';
            }
            if (!values.city) {
              errors.city = 'Please enter the city name.';
            }
            if (!values.state) {
              errors.state = 'Please enter the state.';
            }
            if (!values.zipCode) {
              errors.zipCode = 'Please enter the zip code.';
            } else if (!/^\d{5}(-\d{4})?$/.test(values.zipCode)) {
              errors.zipCode = 'Please enter a valid 5-digit zip code.';
            }
            return errors;
          }}
          onSubmit={async (values, { setSubmitting }) => {
            let updatedValues = values;
            const verifiedAddress = await this.verifyAddress(values);
            if (verifiedAddress) {
              updatedValues = {
                fullName: values.fullName,
                ...verifiedAddress
              };
            }
            this.setState(updatedValues);
            store.set('userInfo', updatedValues);
            setSubmitting(false);
            window.amplitude.getInstance().logEvent('Completed Step 1');
            Onboarding.logCompletedStep(1);
            this.props.goToNextStep();
          }}
        >
          {({ isSubmitting, setFieldValue, values, errors, touched }) => (
            <Form>
              <FormikErrorTracker step={1} errors={errors} touched={touched} values={values} />
              <div className="field">
                <Field id="fullName" name="fullName" placeholder=" " autoComplete="name" />
                <label htmlFor="fullName">Full name</label>
                <ErrorMessage name="fullName" component="div" className="error" />
              </div>
              <div className="field">
                <AddressAutocomplete
                  id="street1"
                  name="street1"
                  autoComplete="street-address"
                  value={values.street1}
                  onChange={v => setFieldValue('street1', v)}
                  onSelect={picked => {
                    setFieldValue('street1', picked.street1);
                    setFieldValue('city', picked.city);
                    setFieldValue('state', picked.state);
                    setFieldValue('zipCode', picked.zipCode);
                    this.setState({ showCityState: true });
                  }}
                />
                <label htmlFor="street1">Street address</label>
                <ErrorMessage name="street1" component="div" className="error" />
              </div>
              {this.state.showStreet2 ? (
                <div className="field">
                  <Field id="street2" name="street2" placeholder=" " autoComplete="address-line2" />
                  <label htmlFor="street2">Apt, suite, unit (optional)</label>
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => {
                      setFieldValue('street2', '');
                      this.setState({ showStreet2: false });
                    }}
                  >
                    − Remove apt/suite
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="link-button"
                  onClick={() => this.setState({ showStreet2: true })}
                >
                  + Add apt/suite #
                </button>
              )}
              <div className="field">
                <Field
                  id="zipCode"
                  name="zipCode"
                  placeholder=" "
                  autoComplete="postal-code"
                  inputMode="numeric"
                  onChange={e => {
                    setFieldValue('zipCode', e.target.value);
                    if (/^\d{5}$/.test(e.target.value)) {
                      this.setState({ showCityState: true });
                      this.lookupZip(e.target.value, setFieldValue);
                    }
                  }}
                />
                <label htmlFor="zipCode">ZIP code</label>
                <ErrorMessage name="zipCode" component="div" className="error" />
              </div>
              {this.state.showCityState && (
                <>
                  <div className="field">
                    <Field id="city" name="city" placeholder=" " autoComplete="address-level2" />
                    <label htmlFor="city">City</label>
                    <ErrorMessage name="city" component="div" className="error" />
                  </div>
                  <div className="field">
                    <Field
                      id="state"
                      component="select"
                      name="state"
                      autoComplete="address-level1"
                      className={values.state ? 'has-value' : ''}
                    >
                      <option value="">State</option>
                      {US_STATES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </Field>
                    <label htmlFor="state">State</label>
                    <ErrorMessage name="state" component="div" className="error" />
                  </div>
                </>
              )}

              <button className="button submit-button submit-button--primary" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Verifying address…' : 'Continue'}
              </button>

              <div className="SignupTrust">
                <div className="SignupTrust__row">
                  <span className="SignupTrust__stars" aria-label="4.9 out of 5 stars">★★★★★</span>
                  <span className="SignupTrust__rating">4.9</span>
                  <span aria-hidden="true">·</span>
                  <span className="SignupTrust__quote">“My mailbox has been extremely peaceful.”</span>
                  <span className="SignupTrust__attribution">— Tom, Brooklyn NY</span>
                </div>
                <p className="SignupTrust__households">Join 3,200+ households already reducing their junk mail.</p>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default StepOneForm;
